/**
 * AdminDataIntegrity — platform-wide data integrity audit dashboard.
 *
 * Lists every registered integrity check with severity, mode (auto/manual),
 * current count of affected docs, last-run timestamp, and a per-row "Fix now"
 * action. A "Run all checks" header button kicks the runner in the background.
 *
 * The audit log at the bottom shows recent auto-fix actions.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/time";
import { ShieldCheck, RefreshCw, Wrench, AlertTriangle, Info, AlertCircle, CheckCircle2, ChevronDown, ChevronRight } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/Ui";

const SEV_STYLES = {
    critical: { color: "text-red-400", border: "border-red-500/40", bg: "bg-red-500/5", Icon: AlertCircle },
    warning:  { color: "text-amber-400", border: "border-amber-500/40", bg: "bg-amber-500/5", Icon: AlertTriangle },
    info:     { color: "text-sky-400", border: "border-sky-500/40", bg: "bg-sky-500/5", Icon: Info },
};

export default function AdminDataIntegrity() {
    const [data, setData] = useState(null);
    const [audit, setAudit] = useState([]);
    const [loading, setLoading] = useState(true);
    const [running, setRunning] = useState(false);
    const [expanded, setExpanded] = useState({});
    const [busyKey, setBusyKey] = useState(null);

    const load = useCallback(async () => {
        try {
            const [c, a] = await Promise.all([
                api.get("/admin/data-integrity/checks"),
                api.get("/admin/data-integrity/audit?limit=50"),
            ]);
            setData(c.data);
            setAudit(a.data?.rows || []);
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const runAll = async () => {
        setRunning(true);
        try {
            const r = await api.post("/admin/data-integrity/run-all");
            toast.success(r.data?.message || "Integrity checks started");
            setTimeout(load, 12000);
        } catch (e) { toast.error(apiError(e)); }
        finally { setRunning(false); }
    };

    const runOne = async (key) => {
        setBusyKey(key);
        try {
            const r = await api.post(`/admin/data-integrity/checks/${key}/run`);
            const fixed = r.data?.autofix_result?.fixed ?? 0;
            toast.success(
                fixed > 0
                    ? `Fixed ${fixed} issue${fixed === 1 ? "" : "s"} (${r.data.total} remaining)`
                    : `Re-checked — ${r.data.total} affected`,
            );
            await load();
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusyKey(null); }
    };

    const toggleMode = async (key, current) => {
        const next = current === "auto" ? "manual" : "auto";
        setBusyKey(key);
        try {
            await api.patch(`/admin/data-integrity/checks/${key}/mode`, { mode: next });
            toast.success(`${key} → ${next}`);
            await load();
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusyKey(null); }
    };

    const checks = data?.checks || [];
    const summary = useMemo(() => {
        let crit = 0, warn = 0, info = 0, totalIssues = 0;
        for (const c of checks) {
            totalIssues += (c.total || 0);
            if (!c.total) continue;
            if (c.severity === "critical") crit++;
            else if (c.severity === "warning") warn++;
            else info++;
        }
        return { crit, warn, info, totalIssues };
    }, [checks]);

    return (
        <div className="space-y-6">
            <PageHeader
                title="Data Integrity"
                subtitle="Platform-wide audits that catch silent data drift. Checks run automatically at 08:30 IST and after every eCourts refresh — auto-fixes are applied for checks set to 'auto'."
                icon={<ShieldCheck className="w-8 h-8" strokeWidth={1.5}/>}
            />

            {/* Summary tiles */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="integrity-summary">
                <SummaryTile label="Total issues" value={summary.totalIssues} accent="text-foreground"/>
                <SummaryTile label="Critical"     value={summary.crit} accent="text-red-400"/>
                <SummaryTile label="Warning"      value={summary.warn} accent="text-amber-400"/>
                <SummaryTile label="Info"         value={summary.info} accent="text-sky-400"/>
            </div>

            {/* Run all + last-run */}
            <div className="flex flex-wrap items-center gap-3 bg-muted border border-border p-4">
                <button onClick={runAll} disabled={running || loading}
                    data-testid="integrity-run-all"
                    className="inline-flex items-center gap-2 px-4 py-2 border border-primary text-primary text-xs uppercase tracking-[0.16em] hover:bg-primary hover:text-primary-foreground disabled:opacity-30">
                    <RefreshCw className={`w-3.5 h-3.5 ${running ? "animate-spin" : ""}`} strokeWidth={1.5}/>
                    {running ? "Queued…" : "Run all checks now"}
                </button>
                {data?.last_aggregate_run && (
                    <span className="text-xs text-muted-foreground">
                        Last run: <span className="text-foreground">{fmtDateTime(data.last_aggregate_run.ran_at)}</span>
                        {" · "}{data.last_aggregate_run.total_fixed} auto-fixed
                        {" · "}{data.last_aggregate_run.critical_unresolved} critical remain
                        {" · source: "}{data.last_aggregate_run.source}
                    </span>
                )}
            </div>

            {/* Per-check rows */}
            <div className="space-y-2">
                {loading && <div className="text-xs text-muted-foreground p-4">Loading checks…</div>}
                {checks.map(c => <CheckRow key={c.key} c={c} expanded={!!expanded[c.key]}
                    onToggleExpand={() => setExpanded(p => ({ ...p, [c.key]: !p[c.key] }))}
                    onRun={() => runOne(c.key)} onToggleMode={() => toggleMode(c.key, c.mode)}
                    busy={busyKey === c.key}/>)}
                {!loading && checks.length === 0 && (
                    <div className="text-xs text-muted-foreground p-4 bg-muted border border-border">No checks registered.</div>
                )}
            </div>

            {/* Audit log */}
            <div>
                <div className="overline text-primary mb-2">Recent auto-fix audit log</div>
                <div className="overflow-x-auto bg-muted border border-border">
                    <table className="w-full text-xs">
                        <thead className="bg-background/30 text-muted-foreground uppercase tracking-[0.16em]">
                            <tr>
                                <th className="text-left p-3">Ran at (IST)</th>
                                <th className="text-left p-3">Check</th>
                                <th className="text-left p-3">Mode</th>
                                <th className="text-right p-3">Fixed</th>
                                <th className="text-left p-3">Errors</th>
                            </tr>
                        </thead>
                        <tbody>
                            {audit.length === 0 && (
                                <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">No auto-fix actions yet.</td></tr>
                            )}
                            {audit.map((r, i) => (
                                <tr key={i} className="border-t border-border" data-testid={`integrity-audit-row-${i}`}>
                                    <td className="p-3 text-foreground font-mono text-[11px]">{fmtDateTime(r.ran_at)}</td>
                                    <td className="p-3 text-foreground">{r.check_key}</td>
                                    <td className="p-3 text-muted-foreground">{r.mode}</td>
                                    <td className="p-3 text-right text-emerald-400 font-mono">{r.fixed}</td>
                                    <td className="p-3 text-red-400">{(r.errors || []).join("; ") || "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}


function SummaryTile({ label, value, accent }) {
    return (
        <div className="bg-muted border border-border p-4">
            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">{label}</div>
            <div className={`text-3xl font-light ${accent || ""}`} data-testid={`integrity-tile-${label.toLowerCase().replace(/\s+/g, "-")}`}>{value ?? 0}</div>
        </div>
    );
}


function CheckRow({ c, expanded, onToggleExpand, onRun, onToggleMode, busy }) {
    const sev = SEV_STYLES[c.severity] || SEV_STYLES.info;
    const { Icon } = sev;
    const ok = (c.total ?? 0) === 0;
    return (
        <div className={`border ${ok ? "border-border" : sev.border} ${ok ? "bg-muted" : sev.bg} transition-colors`} data-testid={`integrity-check-${c.key}`}>
            <div className="flex items-center gap-3 p-4">
                <button onClick={onToggleExpand} className="text-muted-foreground hover:text-foreground" aria-label="expand">
                    {expanded ? <ChevronDown className="w-4 h-4"/> : <ChevronRight className="w-4 h-4"/>}
                </button>
                <Icon className={`w-5 h-5 ${ok ? "text-emerald-400" : sev.color}`} strokeWidth={1.5}/>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm text-foreground">{c.label}</span>
                        <span className={`text-[10px] px-2 py-0.5 uppercase tracking-[0.16em] ${sev.color} border ${sev.border}`}>{c.severity}</span>
                        {ok ? (
                            <span className="text-[10px] inline-flex items-center gap-1 text-emerald-400">
                                <CheckCircle2 className="w-3 h-3"/> healthy
                            </span>
                        ) : (
                            <span className={`text-[10px] ${sev.color}`}>{c.total} affected</span>
                        )}
                    </div>
                    {c.last_run_at && (
                        <div className="text-[10px] text-muted-foreground mt-0.5 font-mono">
                            Last: {fmtDateTime(c.last_run_at)}
                            {c.autofix_done && c.autofix_result && ` · fixed ${c.autofix_result.fixed || 0}`}
                        </div>
                    )}
                </div>
                <button onClick={onToggleMode} disabled={busy}
                    data-testid={`integrity-mode-toggle-${c.key}`}
                    className={`text-[10px] uppercase tracking-[0.16em] px-3 py-1 border ${c.mode === "auto" ? "border-emerald-500/40 text-emerald-400" : "border-border text-muted-foreground"} hover:bg-primary/10 disabled:opacity-30`}>
                    {c.mode}
                </button>
                <button onClick={onRun} disabled={busy}
                    data-testid={`integrity-fix-${c.key}`}
                    title={c.has_autofix ? c.autofix_label : "Re-check (no autofix available)"}
                    className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] px-3 py-1 border border-primary text-primary hover:bg-primary hover:text-primary-foreground disabled:opacity-30">
                    <Wrench className="w-3 h-3" strokeWidth={1.5}/>
                    {busy ? "…" : (c.has_autofix ? "Fix now" : "Re-check")}
                </button>
            </div>
            {expanded && (
                <div className="border-t border-border px-4 py-3 text-xs space-y-3">
                    <div className="text-muted-foreground">{c.description}</div>
                    {c.has_autofix && (
                        <div className="text-emerald-400 text-[11px]">
                            Auto-fix: {c.autofix_label}
                        </div>
                    )}
                    {(c.samples || []).length > 0 && (
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-2">Sample affected ({c.samples.length})</div>
                            <div className="space-y-1 font-mono text-[11px]">
                                {c.samples.map((s, i) => (
                                    <div key={i} className="text-muted-foreground">
                                        <span className="text-foreground">{s.id}</span>
                                        {s.tenant_id && <span> · tenant={s.tenant_id.slice(0, 8)}…</span>}
                                        {s.snapshot && Object.keys(s.snapshot).length > 0 && (
                                            <span> · {JSON.stringify(s.snapshot)}</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {c.by_tenant && Object.keys(c.by_tenant).length > 0 && (
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-2">Affected tenants ({Object.keys(c.by_tenant).length})</div>
                            <div className="text-[11px] font-mono text-muted-foreground">
                                {Object.entries(c.by_tenant).slice(0, 10).map(([t, n]) => (
                                    <span key={t} className="mr-3">{t.slice(0, 8)}…: {n}</span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
